'use strict';

import {each} from 'underscore';
import LZString from "lz-string";
import { parse, stringify } from 'qs';

import history from "./History";
import {isEmpty} from './Utils';
import StateValidator from './StateValidator';
import { parseJson } from './JsonParseUtils';

/*
 * Persists component state using HTML5 history API to overwrite the app URL.
 *
 * To use it your Store has to provide a unique component id and a getDefaultState() method which returns the default
 * state object for your component. This object will be used to validate objects found in the URL.
 */
class UrlPersistenceStore {

    constructor(logger) {
        /*
         * Storage for persisted component data.
         *
         * This is a map where the key is the component id and the value is a data structure like ...
         *
         *   {
         *      defaultDataMethod: functionReference,
         *      data: {*}
         *   }
         *
         * The 'defaultDataMethod' property is supplied when a persisted component is initialized. The 'data' property
         * is populated when the component persists data to the URL and kind of serves as a cache (so we don't parse
         * the URL unnecessarily).
         */
        this.componentData = {};
        this.logger = logger;
        this.isDebug = false;
    }

    /**
     * Registers a component. Stores that need to persist data should call this method from their constuctors.
     * If the given id already exists it will be overwritten.
     *
     * @param id The unique component id/key.
     * @param defaultDataMethod A callback method for default data.
     */
    register(id, defaultDataMethod) {
        if (! defaultDataMethod) {
            this.logger.error(`A default data method is required for component [${id}].`);
        }

        if (isEmpty(defaultDataMethod())) {
            this.logger.error(`The default data method for component [${id}] returned an empty object.`);
        }

        this.componentData[id] = {
            defaultDataMethod: defaultDataMethod,
            data: undefined
        }
    }

    /**
     * Attempts to read persisted component data from the URL. If component data is found the data object is returned.
     * Otherwise, it returns null.
     * @param componentId The unique Id of the component.
     * @return
     */
    getPersistedData(componentId) {
        // Determine if the data object for the component exists already.
        const dataObject = this.componentData[componentId];
        if (! dataObject) {
            this.logger.log(`Component [${componentId}] was not registered for URL persistence.`);
            return null;
        }

        // See if any data is currently stored on the data object. If so, just return that.
        if (dataObject.data) {
            return dataObject.data;
        } else if(window.location.hash.indexOf("?") > -1 && window.location.hash.indexOf("states") > -1) {
             // Otherwise, if the url contains states param then parse the URL data and see if the component data turns up.
            this.parseRequestParams(window.location.hash.substring(window.location.hash.indexOf("?")));
            dataObject.data = this.componentData[componentId].data;
        } else {
            return null;
        }
        return dataObject.data;
    }

    /**
     * Parses the URL request params and stores component data objects for access via getPersistedData(id).
     */
    parseRequestParams(requestParams) {
        //  Bail out if there aren't any request parameters.
        if (! requestParams) {
            return;
        }

        // Turn the request params into a JSON object.
        let requestParamsObject = parse(requestParams,{ ignoreQueryPrefix: true });
        if (isEmpty(requestParamsObject)) {
            // Bail out if the request params aren't parsable.
            this.logger.error("UrlState-parseRequestParams-parseFailed", {requestParams: requestParams});
            return;
        }

        // Grab the 'states' param (if it exists)
        const compressedDataObjects = requestParamsObject.states;
        let uncompressedDataObjects = null;
        //  Uncompress its value into an object string representation.
        if (compressedDataObjects) {
            uncompressedDataObjects = LZString.decompressFromEncodedURIComponent(compressedDataObjects);
        }

        /*
         * Parse the "states" string into an object that looks like this ...
         *
         *     {key: "JSON String", ...}
         */
        let componentDataObjects = parseJson(uncompressedDataObjects);
        if (! componentDataObjects ) {
            this.logger.error("UrlState-parseStates-parseFailed", {states: uncompressedDataObjects});
            return;
        }

        /*
         * For each component/id, parse the value, validate it, and save it.
         */
        for (let key in componentDataObjects) {
            const stringObj = componentDataObjects[key];
            let obj = parseJson(stringObj);
            if (! obj ) {
                this.logger.error("UrlState-parseData-parseFailed", {do: stringObj});
            }

            const componentDataObject = this.componentData[key];
            // If there isn't a registered data object for the given key then just skip it.
            if (componentDataObject) {
                //  Validate the data against default data.
                const valid = StateValidator.isStateValid(componentDataObject.defaultDataMethod(), obj, null, this.isDebug);
                if (valid) {
                    this.componentData[key].data = obj;
                } else {
                    this.logger.error(`Component data for [${key}] was invalid.`, {states: uncompressedDataObjects});
                }
            }
        }
    }

    /**
     * Returns the component state as seen in componentStates if the state exists.
     * Otherwise, returns null.
     */
    getComponentState(componentId) {
        //If componentStates contain the componentId then we use what we have stored.
        if (this.componentStates && this.componentStates[componentId]) {
            return this.componentStates[componentId];
        }
        //If componentStates are not present and if the url contains states param then we parse the request to get the state.
        else if(window.location.hash.indexOf("?") > -1 && window.location.hash.indexOf("states") > -1) {
            this.parseRequestParams(window.location.hash.substring(window.location.hash.indexOf("?")));
            return this.componentStates[componentId];
        } else{
            return null;
        }
    }

    /**
     * Merges component data into the states param in the current browser history entry / url, removing the
     * specified list of request parameters.
     *
     * @param componentId The component key.
     * @param newData The new data object to persist.
     * @param removeReqParamList (optional) A list of param keys to exclude.
     */
    pushComponentDataToUrl(componentId, newData, removeReqParamList) {
        if (removeReqParamList && (removeReqParamList.constructor !== Array)) {
            console.log('The argument \'removeReqParmList\' to method \'pushStateToUrl\'  should be an Array');
        }

        let dataObject = this.componentData[componentId];
        if (! dataObject) {
            throw "FIXME in UrlPersistenceStore.pushComponentDataToUrl: How did this happen?";
        }

        // Store the new data.
        dataObject.data = newData;

        // AppActions.pushComponentStateToUrl(componentId, this.urlDataObject, removeReqParamList);

        /*
         * Parse the existing request params from the URL.
         */
        const href = location.href;
        let requestParams = href.split("?")[1];
        let paramMap = {};
        // If there are request params then turn into an object for easy access to props.
        if (requestParams) {
            paramMap = parse(requestParams);
            if (isEmpty(paramMap)) {
                // This is not an error condition. Just log and move on
                this.logger.log("UrlState-PushToUrl-parseFailed", {requestParams: requestParams});
            }
            if (removeReqParamList) {
                each(removeReqParamList, function (removeReqParam) {
                    delete paramMap[removeReqParam];
                });
            }
        }

        //  Pull out the 'states' param, uncompress it, then parse it into an object.
        let statesCompressed = paramMap.states;
        let statesString = null;
        if (statesCompressed) {
            statesString = LZString.decompressFromEncodedURIComponent(statesCompressed);
        } else {
            statesString = "";
        }

        let states = {};
        if (statesString) {
            states = parseJson(statesString);
            if ( !states ){
                // This is fatal, so log and bail.
                this.logger.error(`Could not parse decoded states value. [${statesString}]`);
                return;
            }
        }

        // Add the key with a stringified representation of the object. (Or overwrite the value if the key already exists).
        states[componentId] = JSON.stringify(newData);

        // Re-parse all of the entries so that they are stored correctly.
        // For each key, parse the value and overwrite the string value with the object value.
        for (let key in states) {
            let obj = parseJson(states[key]);
            if ( obj ) {
                this.componentData[key].data = obj;
            } else {
                this.logger.error(`Could not parse data object for component [${key}].`);
            }
        }

        // Stringify and compress the entire states object, then write it back to the 'states' param on paramMap.
        let statesParamValue = LZString.compressToEncodedURIComponent(JSON.stringify(states));
        paramMap.states = statesParamValue;

        // Turn the param map back into a string.
        let paramsString = stringify(paramMap);

        // Replace the current URL / browser history entry with the new URL. Note: This operation will not reload the page.
        let context = location.pathname;

        // Match for everything before the ?
        let hashToQuestionMark = location.hash.match(/[^?]*/);
        if (! hashToQuestionMark) {
            hashToQuestionMark = location.hash + "?";
        } else {
            hashToQuestionMark = hashToQuestionMark + "?";
        }
        history.replace(`${context}${hashToQuestionMark}${paramsString}`);
    }

    /**
     * Turn on/off debugging.
     * @param debug
     */
    setDebug(debug) {
        this.isDebug = debug;
    }
}

export default UrlPersistenceStore;